/**
 * React hook for Server-Sent Events (SSE) game updates
 * TypeScript version with built-in query invalidation
 *
 * @example Simple usage (just invalidates queries)
 * useGameUpdates({
 *   purchases: true,
 *   trades: true,
 *   notifications: true,
 * });
 *
 * @example Advanced usage (with custom callbacks)
 * useGameUpdates({
 *   purchases: true,
 *   notifications: () => toast.info('New notification!'),
 *   achievements: async () => {
 *     const data = await queryClient.refetchQueries({ queryKey: ['achievements'] });
 *     showAchievementModal(data[0]?.data?.results?.[0]);
 *   },
 * });
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// Query key mapping (camelCase to query keys)
const QUERY_KEY_MAP: Record<string, string> = {
  map_tiles: '/all/map-tiles/',
  purchases: '/all/purchases/',
  trades: '/all/trades/',
  new_trades: '/all/new-trades/',
  military_alliances: 'militaryAlliances',
  trade_alliances: 'tradeAlliances',
  new_military_alliances: 'militaryAlliances',
  new_trade_alliances: 'tradeAlliances',
  peace_treaties: 'peaceTreaties',
  wars: 'wars',
  warnings: 'warnings',
  subpoenas: 'subpoenas',
  attacks: '/all/attacks/',
  repairs: '/all/repairs/',
  resources: '/all/resources/',
  notifications: '/all/notifications/',
  achievements: '/all/user-class-achievements/',
  notes: '/all/notes/',
};

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // Close SSE after 5 minutes without user activity

export function useGameUpdates() {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const permanentErrorRef = useRef(false);
  const endpoint = import.meta.env.VITE_API_BASE_URL + '/all/game-events/';
  const debug = false;

  useEffect(() => {
    let inactivityTimeoutId: number | null = null;

    const clearInactivityTimer = () => {
      if (inactivityTimeoutId !== null) {
        window.clearTimeout(inactivityTimeoutId);
        inactivityTimeoutId = null;
      }
    };

    const closeEventSource = (reason: string) => {
      if (eventSourceRef.current) {
        if (debug) console.log(`SSE: Closing connection (${reason})`);
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };

    const startInactivityTimer = () => {
      clearInactivityTimer();
      inactivityTimeoutId = window.setTimeout(() => {
        closeEventSource('inactivity');
        inactivityTimeoutId = null;
      }, INACTIVITY_TIMEOUT_MS);
    };

    const openEventSource = (first: boolean) => {
      if (eventSourceRef.current) return;
      if (permanentErrorRef.current) return;

      const eventSource = new EventSource(endpoint, { withCredentials: true });
      eventSourceRef.current = eventSource;
      let connected = false;

      if (debug) console.log('SSE: Connecting to game updates...');

      eventSource.onopen = () => {
        connected = true;
        if (debug) console.log('SSE: Connected to game updates');
      };

      eventSource.onmessage = (event) => {
        try {
          const updates = JSON.parse(event.data);

          if (debug) {
            console.log('SSE: Received updates:', updates);
          }

          if (!updates['is_initial'] || !first) {
            Object.entries(updates).forEach(([sseKey, timestamp]) => {
              if (debug) {
                console.log(`SSE: ${sseKey} changed at ${timestamp}`);
              }

              const queryKey = QUERY_KEY_MAP[sseKey];
              if (queryKey) {
                // Add random jitter (0-2 seconds) to spread out server load
                // when many clients receive updates simultaneously
                const jitterMs = Math.random() * 2000;

                setTimeout(() => {
                  queryClient.invalidateQueries({ queryKey: [queryKey] });
                  if (queryKey == "/all/notifications/") {
                    queryClient.invalidateQueries({ queryKey: ["/all/user-notifs/"] });
                  }
                  if (debug) {
                    console.log(`SSE: Invalidated query [${queryKey}] after ${jitterMs.toFixed(0)}ms jitter`);
                  }
                }, jitterMs);
              }
            });
          }
        } catch (err) {
          console.error('SSE: Error parsing event data:', err);
        }
      };

      eventSource.onerror = (error) => {
        if (!connected) {
          console.error('SSE: Permanent connection error (never connected), disabling reconnect:', error);
          permanentErrorRef.current = true;
        } else {
          if (debug) console.log('SSE: Will attempt to reconnect on next activity');
        }
        closeEventSource('error');
      };
    };

    const handleActivity = () => {
      startInactivityTimer();
      if (!eventSourceRef.current) {
        if (debug) console.log('SSE: Resuming connection after activity');
        openEventSource(false);
      }
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'focus',
    ];

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleActivity();
      }
    };

    openEventSource(true);
    startInactivityTimer();

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity);
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInactivityTimer();
      closeEventSource('cleanup');
    };
  }, [
    endpoint,
    debug,
    queryClient,
  ]);

  return eventSourceRef;
}
